import { useState, useEffect, useRef, useCallback } from 'react';
import * as api from '../api';
import { INITIAL_STATUS_COLORS, ALBUM_STATUS_COLORS, INITIAL_COLUMNS, INITIAL_THEME } from '../constants';

const SYNC_QUEUE_KEY = 'photofine_sync_queue';
const MAX_RETRIES = 3;
const MIN_SYNC_INTERVAL = 5000; // 5 seconds between syncs
const MAX_SYNC_PER_MINUTE = 12; // Safety limit

function loadQueue() {
  try {
    return JSON.parse(localStorage.getItem(SYNC_QUEUE_KEY) || '[]');
  } catch (error) { return []; }
}

function saveQueue(queue) {
  localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(queue));
}

export function useSync(currentUser, setters, getBrazilDate) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const isSyncingRef = useRef(false);
  const syncTimeoutRef = useRef(null);
  const lastSyncRef = useRef(0);
  const lastSyncEndRef = useRef(0); // Timestamp when last sync completed
  const syncCountWindowRef = useRef([]); // Timestamps of recent syncs
  const SYNC_COOLDOWN_MS = 5000; // No saves allowed for 5s after sync completes
  const serverSaveTimerRef = useRef(null); // Debounce timer for server saves
  const pendingServerSavesRef = useRef({}); // Accumulated pending saves
  const lastLocalEditRef = useRef(0); // Timestamp of last local edit (to prevent sync overwrite)
  // Use ref for setters to prevent infinite loops when component re-renders
  const settersRef = useRef(setters);
  useEffect(() => { settersRef.current = setters; }, [setters]);

  // Debug: log sync frequency
  const checkSyncFrequency = () => {
    const now = Date.now();
    syncCountWindowRef.current = syncCountWindowRef.current.filter(t => now - t < 60000);
    syncCountWindowRef.current.push(now);
    const count = syncCountWindowRef.current.length;
    if (count > MAX_SYNC_PER_MINUTE) {
      console.error('🔴 SYNC LOOP DETECTED! Syncs in last 60s:', count, '— suppressing further syncs');
      return false; // Block sync
    }
    return true; // Allow sync
  };

  // Monitor online/offline
  useEffect(() => {
    const handleOnline = () => { console.log('🟢 Network: online'); setIsOnline(true); };
    const handleOffline = () => { console.log('🔴 Network: offline'); setIsOnline(false); };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Full sync from server
  const syncFromServer = useCallback(async (source = 'unknown') => {
    if (!currentUser) return;
    // Throttle: min interval between syncs
    const now = Date.now();
    const elapsed = now - lastSyncRef.current;
    if (elapsed < MIN_SYNC_INTERVAL) {
      console.warn('⏳ syncFromServer throttled —', elapsed, 'ms since last sync, min:', MIN_SYNC_INTERVAL, 'source:', source);
      return;
    }
    // Safety: detect loop
    if (!checkSyncFrequency()) return;
    // Prevent concurrent syncs
    if (isSyncingRef.current) {
      console.warn('⏳ syncFromServer skipped — already syncing, source:', source);
      return;
    }

    console.log('🔄 syncFromServer START — source:', source, 'user:', currentUser?.email);
    lastSyncRef.current = now;
    isSyncingRef.current = true;
    setIsSyncing(true);
    try {
      const data = await api.fetchData();
      console.log('✅ syncFromServer OK — tasks:', data.tasks?.length, 'groups:', data.groups?.length);

      // Protection: don't overwrite state if there was a local edit in the last 3s
      const timeSinceLocalEdit = Date.now() - lastLocalEditRef.current;
      if (timeSinceLocalEdit < 3000) {
        console.log('⏳ Sync deferred — local edit recent (', timeSinceLocalEdit, 'ms ago), source:', source);
        isSyncingRef.current = false;
        setIsSyncing(false);
        return;
      }

      const s = settersRef.current;
      if (data.tasks) s.setTasks(data.tasks);
      if (data.groups) s.setGroups(data.groups);
      if (data.categories) s.setCategories(data.categories);
      if (data.statusOptions) s.setStatusOptions(data.statusOptions);
      if (data.columns) s.setColumns(data.columns);
      if (data.members) s.setMembers(data.members);
      if (data.theme) s.setTheme(prev => ({ ...INITIAL_THEME, ...data.theme }));
      if (data.albums) s.setAlbums(data.albums);
      if (data.albumStatusOptions) s.setAlbumStatusOptions(data.albumStatusOptions);
      if (data.hiddenColumns) s.setHiddenColumns(data.hiddenColumns);
      if (data.notificationSettings && s.setNotificationSettings) s.setNotificationSettings(data.notificationSettings);
      if (data.notifications) {
        const localData = JSON.parse(localStorage.getItem('photofine_data') || '{}');
        const localNotifs = localData.notifications || [];
        const serverNotifs = data.notifications.filter(n =>
          !n.recipientId || n.recipientId === currentUser.id
        );
        const mergedNotifs = [
          ...serverNotifs,
          ...localNotifs.filter(n => !data.notifications.some(sn => sn.id === n.id))
        ];
        s.setNotifications(mergedNotifs);
        s.setUnreadCount(mergedNotifs.filter(n => !n.read).length);
        // NOTE: Do NOT call saveStateRef.current here — it triggers enqueueSave → api.saveData → socket broadcast → syncFromServer = LOOP!
        // Just update localStorage directly below
      }

      // Update localStorage with server data (no server save — prevents loop)
      const dataToSave = {
        tasks: data.tasks || [],
        groups: data.groups || [],
        categories: data.categories || [],
        statusOptions: data.statusOptions || INITIAL_STATUS_COLORS,
        columns: data.columns || INITIAL_COLUMNS,
        members: data.members || [],
        theme: data.theme || INITIAL_THEME,
        albums: data.albums || [],
        albumStatusOptions: data.albumStatusOptions || ALBUM_STATUS_COLORS,
        hiddenColumns: data.hiddenColumns || [],
        notificationSettings: data.notificationSettings || {},
        notifications: data.notifications || [],
        updatedAt: getBrazilDate().toISOString()
      };
      localStorage.setItem('photofine_data', JSON.stringify(dataToSave));
    } catch (error) {
      console.warn('⚠️ syncFromServer FAILED:', error.message, 'source:', source);
    } finally {
      isSyncingRef.current = false;
      setIsSyncing(false);
      lastSyncEndRef.current = Date.now(); // Mark sync end for cooldown
      console.log('🔄 syncFromServer END — source:', source);
    }
  }, [currentUser, getBrazilDate]);

  // Flush offline queue when coming back online
  const flushQueue = useCallback(async () => {
    const queue = loadQueue();
    if (queue.length === 0) return;
    console.log('📤 Flushing queue —', queue.length, 'items');

    const remaining = [];
    for (const item of queue) {
      try {
        await api.saveData(item.updates);
        console.log('📤 Queue item flushed:', item.id);
      } catch (err) {
        item.retries = (item.retries || 0) + 1;
        if (item.retries < MAX_RETRIES) {
          remaining.push(item);
        } else {
          console.warn('❌ Queue item dropped after', MAX_RETRIES, 'retries:', item.id);
        }
      }
    }
    saveQueue(remaining);
  }, []);

  // When coming back online, flush queue then sync
  useEffect(() => {
    if (isOnline && currentUser) {
      console.log('🟢 Back online — flushing queue + syncing');
      flushQueue().then(() => syncFromServer('back-online'));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, currentUser]);

  // Enqueue a save for offline persistence
  const enqueueSave = useCallback((updates) => {
    if (!currentUser) return;

    // Record local edit timestamp
    lastLocalEditRef.current = Date.now();

    // Always save to localStorage immediately
    try {
      const existingData = localStorage.getItem('photofine_data');
      const currentData = existingData ? JSON.parse(existingData) : {};
      const dataToSave = { ...currentData, ...updates, updatedAt: getBrazilDate().toISOString() };
      localStorage.setItem('photofine_data', JSON.stringify(dataToSave));
    } catch (e) {
      console.error('Erro ao salvar localStorage:', e);
    }

    // Accumulate changes and debounce server save (800ms)
    pendingServerSavesRef.current = { ...pendingServerSavesRef.current, ...updates };
    if (serverSaveTimerRef.current) clearTimeout(serverSaveTimerRef.current);
    serverSaveTimerRef.current = setTimeout(() => {
      const pending = { ...pendingServerSavesRef.current };
      pendingServerSavesRef.current = {};
      serverSaveTimerRef.current = null;
      if (Object.keys(pending).length === 0) return;

      if (isSyncingRef.current) {
        console.warn('⏳ Server save skipped — syncing in progress, re-queuing');
        pendingServerSavesRef.current = { ...pending };
        return;
      }
      // Cooldown: don't save for 5s after sync completes
      const timeSinceSync = Date.now() - lastSyncEndRef.current;
      if (lastSyncEndRef.current > 0 && timeSinceSync < SYNC_COOLDOWN_MS) {
        console.warn('⏳ Server save skipped — sync cooldown, re-queuing');
        pendingServerSavesRef.current = { ...pending };
        return;
      }

      const keys = Object.keys(pending);
      console.log('💾 Debounced server save — keys:', keys.join(', '));

      // Try server save; if offline, queue it
      if (isOnline) {
        api.saveData(pending).then(() => {
          console.log('💾 Server save OK — keys:', keys.join(', '));
        }).catch(err => {
          if (err.status === 429) {
            console.warn('💾 Server save rate limited (429) — skipping');
            return;
          }
          console.warn('💾 Server save FAILED, queuing:', err.message);
          const queue = loadQueue();
          queue.push({ id: Date.now(), updates: pending, retries: 0 });
          saveQueue(queue);
        });
      } else {
        const queue = loadQueue();
        queue.push({ id: Date.now(), updates: pending, retries: 0 });
        saveQueue(queue);
      }
    }, 800);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, isOnline, getBrazilDate]);

  // Socket.io + initial sync on login
  useEffect(() => {
    if (!currentUser) return;

    console.log('🔌 useSync: setting up socket for', currentUser.email);
    syncFromServer('login');

    const debouncedSync = () => {
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = setTimeout(() => syncFromServer('socket-event'), 2000);
    };

    api.connectSocket(
      (payload) => {
        console.log('🔌 Socket data-updated — updatedBy:', payload.updatedBy, 'me:', currentUser?.id);
        // Type-safe comparison: convert both to String to avoid 4 !== "4" mismatch
        if (String(payload.updatedBy) !== String(currentUser?.id) || payload.fullReload) {
          debouncedSync();
        }
      },
      () => {
        console.log('🔌 Socket file event');
        debouncedSync();
      }
    );

    return () => {
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
      api.disconnectSocket();
      console.log('🔌 useSync: socket disconnected');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  return {
    isOnline,
    isSyncing,
    isSyncingRef,
    enqueueSave,
    syncFromServer,
  };
}

